import { action, observable, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosError } from 'axios';

import { reverse } from 'named-urls';

import Logger from 'utils/logger';

import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { EntityValueType, AssessmentResponse } from 'utils/api/modules/entities/entities.types';
import { formErrors, isNotFound } from 'utils/api/errors';

import { EntityModules } from 'core/constants';

import routes from 'core/routes';

import EntitiesActivities from 'stores/EntitiesActivities';

import RootStore from '../Root';
import EntityDetails from '../EntityDetails/EntityDetails';

const MappedErrorMessages = {
  hazard: 'Hazard',
  harmed: 'Harmed',
  existingControls: 'Existing Controls',
  severityInitial: 'Initial Severity',
  likelihoodInitial: 'Initial Likelihood',
  riskScoreInitial: 'Initial Risk Score',
  severityResidual: 'Residual Severity',
  likelihoodResidual: 'Residual Likelihood',
};

export default class RiskAssessmentDetailsStore extends EntityDetails {
  @observable assessments?: AssessmentResponse[];

  @observable loadingAssessments = false;

  constructor(rootStore: RootStore, api: typeof API) {
    super(
      rootStore,
      api,
      EntityModules.RisksAssessments,
      routes.dashboard.riskAssessments.toString(),
      (uuid: string): string => reverse(routes.dashboard.riskAssessments.details, { entityId: uuid })
    );

    makeObservable(this);

    eventBus.register(this);
  }

  @action.bound
  async loadDetails(riskAssessmentId: string): Promise<void> {
    this.isLoaded = false;
    try {
      const { data } = await this.api.entities.loadEntitiesDetails(this.entityType, riskAssessmentId)();
      runInAction(() => {
        this.activities = new EntitiesActivities(this.store, this.api, riskAssessmentId, this.entityType);
        this.error = undefined;
        this.details = data;
      });

      // load assessments
      this.loadAssessment(riskAssessmentId);
    } catch (e) {
      runInAction(() => {
        // @ts-ignore
        this.error = e;
      });
      // @ts-ignore
      if (!isNotFound(e)) {
        Logger.error(`Invalid load risk assessment details API response. Risk assessment id: ${riskAssessmentId}`, e);
      }
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  // Assessment
  @action.bound
  async loadAssessment(riskAssessmentId: string): Promise<void> {
    this.loadingAssessments = true;
    try {
      const { data } = await this.api.entities.loadEntitiesAssessments(this.entityType, riskAssessmentId)();
      runInAction(() => {
        this.assessments = data;
      });
    } catch (e) {
      Logger.error('Failed to load assessment', e);
    } finally {
      runInAction(() => {
        this.loadingAssessments = false;
      });
    }
  }

  // add assessment
  @action.bound
  async addAssessment(data: Partial<AssessmentResponse>): Promise<AssessmentResponse[]> {
    if (!this.details?.uuid) {
      return [];
    }

    try {
      const { data: assessments } = await this.api.entities.addEntitiesAssessments(
        this.entityType,
        this.details.uuid,
        data
      )();
      runInAction(() => {
        this.assessments = assessments;
      });

      this.store.notification.enqueueSuccessSnackbar('Assessment added successfully');
      return assessments;
    } catch (e) {
      Logger.error('Failed to add assessment', e);
      const error = e as AxiosError;
      const errorData = formErrors(error).fieldErrors?.assessment;
      // messaage key: value
      const errorMessage = Object.entries(errorData || {}).map(
        ([key, value]) => `${MappedErrorMessages[key as keyof typeof MappedErrorMessages]}: ${value}`
      );
      for (const message of errorMessage) {
        this.store.notification.enqueueErrorSnackbar(message);
      }
      return [];
    }
  }

  // delete assessment
  @action.bound
  async deleteAssessment(assessmentId: string): Promise<boolean> {
    if (!this.details?.uuid) {
      return false;
    }
    try {
      await this.api.entities.deleteEntitiesAssessments(this.entityType, this.details.uuid, assessmentId)();
      runInAction(() => {
        this.assessments = this.assessments?.filter((a) => a.uuid !== assessmentId);
      });
      this.store.notification.enqueueSuccessSnackbar('Assessment deleted successfully');
      return true;
    } catch (e) {
      Logger.error('Failed to delete assessment', e);
      this.store.notification.enqueueErrorSnackbar('Failed to delete assessment');
      return false;
    }
  }

  // edit assessment
  @action.bound
  async editAssessment(assessmentId: string, data: Partial<AssessmentResponse>): Promise<boolean> {
    if (!this.details?.uuid) {
      return false;
    }
    try {
      const { data: assessment } = await this.api.entities.editEntitiesAssessments(
        this.entityType,
        this.details.uuid,
        assessmentId,
        data
      )();
      runInAction(() => {
        this.assessments = this.assessments?.map((a) => (a.uuid === assessmentId ? assessment : a));
      });
      this.store.notification.enqueueSuccessSnackbar('Assessment updated successfully');
      return true;
    } catch (e) {
      Logger.error('Failed to edit assessment', e);
      this.store.notification.enqueueErrorSnackbar('Failed to edit assessment');
      return false;
    }
  }

  @action.bound
  create = async (riskAssessmentName: string): Promise<boolean | string> => {
    try {
      const {
        data: { uuid },
      } = await this.api.entities.createEntity(this.entityType, { [`${this.prefix}name`]: riskAssessmentName })();

      return uuid;
    } catch (e) {
      Logger.error('Failed to create risk assessment', e);

      return false;
    }
  };

  delete = async (riskAssessmentId: string): Promise<boolean> => {
    try {
      await this.api.entities.deleteEntity(this.entityType, riskAssessmentId)();
    } catch (e) {
      Logger.error('Failed to delete risk assessment', e);
      return false;
    }
    return true;
  };

  edit = async (data: Record<string, EntityValueType>): Promise<boolean> => {
    if (this.details?.uuid === undefined) {
      return false;
    }
    try {
      const edited = await this.api.entities.editEntity(this.entityType, this.details?.uuid, data)();
      this.details = edited.data;
      this.isContentEdited = false;
      this.store.notification.enqueueSuccessSnackbar('Risk assessment edited successfully');
      return true;
    } catch (e) {
      const error = e as AxiosError<string[]>;
      this.store.notification.enqueueErrorSnackbar(
        error.response?.data?.[0] || `The risk assessment could not be updated. Something went wrong.`
      );
      Logger.error('Failed to edit risk assessment', e);
      return false;
    }
  };

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.details = undefined;
  }

  @action.bound
  async loadCount(): Promise<number> {
    this.isLoaded = false;
    try {
      const { data } = await this.api.entities.loadEntitiesCount(EntityModules.RisksAssessments)();
      runInAction(() => {
        this.error = undefined;
      });
      return data.count;
    } catch (e) {
      runInAction(() => {
        // @ts-ignore
        this.error = e;
      });
      return 0;
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  @action.bound
  contentHasBeenEdited(questionId: string, newValue: AnswerTypes): void {
    const question = this.details?.entity[questionId];
    if (question?.value !== newValue) {
      this.isContentEdited = true;
    } else {
      this.isContentEdited = false;
    }
  }
}
